import { Table, TableProps } from 'antd';
import { ColumnProps, ColumnType } from 'antd/es/table';
import { useTranslation } from 'translations/hooks';

import { PlotterMode } from 'business/data-analysis/constants';
import { Y_SCROLL_TRIGGER_VALUE } from 'business/data-analysis/pages/graph/components/graph-table-view/constants';
import { useGraphSet } from 'business/data-analysis/pages/graph/hooks/graph-context/contexts';
import { isRingParameter } from 'business/data-analysis/pages/graph/hooks/graph-context/is-ring-parameter';
import { useGraphSetData } from 'business/data-analysis/pages/graph/hooks/graph-data-context/contexts';
import { useMode } from 'business/data-analysis/pages/graph/hooks/use-mode';
import { parseAndFormatDateForGraph } from 'business/data-analysis/services/parse-and-format-date-for-graph';

import { useGraph } from './../../hooks/use-graph';

interface Props {
  graphId: string;
}

const useFirstColumn = (mode: PlotterMode): ColumnType<any> => {
  const { t } = useTranslation();

  const width = 200;
  const fixed = 'left';

  if (mode === PlotterMode.TIME_PERIOD) {
    return {
      title: t('dataAnalysis.timePeriodMode.date'),
      dataIndex: 'date',
      key: 'date',
      fixed,
      width,
      render: (value: string) => {
        return <span>{parseAndFormatDateForGraph(value)}</span>;
      },
    } satisfies ColumnProps;
  }

  return {
    title: t('dataAnalysis.ringMode.ring'),
    dataIndex: 'ring',
    key: 'ring',
    fixed,
    width,
  } satisfies ColumnProps;
};

const GraphTableView = ({ graphId }: Props) => {
  const { language } = useTranslation();
  const mode = useMode();
  const { parameters } = useGraph(graphId);
  const graphSet = useGraphSet();
  const values = useGraphSetData();

  const firstColumn = useFirstColumn(mode);
  const params = Array.from(parameters.values());

  const columns: TableProps['columns'] = [
    firstColumn,
    ...params.map((param) => {
      const key = isRingParameter(graphSet, param)
        ? `${param.id}_${param.aggregationType}_${param.computeType}`
        : param.id;

      return {
        title: `${param.name} (${param.unit})`,
        dataIndex: key,
        key,
        width: 250,
        render: (value: string) => {
          return `${Number(value).toLocaleString(language)}`;
        },
      } satisfies ColumnProps;
    }),
  ];

  return (
    <Table
      columns={columns}
      dataSource={values}
      virtual
      scroll={{ y: Y_SCROLL_TRIGGER_VALUE }}
      pagination={false}
      bordered
      size="small"
    />
  );
};

export default GraphTableView;
